import React, { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, IdentificationCard, ArrowSquareOut, CheckCircle, Warning, XCircle, Bank, Scales, UserCircleGear, HandCoins, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const SEM = {
  verde: { label: "Baixo risco", cls: "status-ativo", color: "#059669", Icon: CheckCircle },
  amarelo: { label: "Atenção", cls: "status-pendente", color: "#D97706", Icon: Warning },
  vermelho: { label: "Alto risco", cls: "status-reprovado", color: "#DC2626", Icon: XCircle },
};
const RECEITA = {
  nao_verificado: "Não verificado", regular: "Regular", pendente_regularizacao: "Pendente de regularização",
  suspensa: "Suspensa", cancelada: "Cancelada", titular_falecido: "Titular falecido", nula: "Nula",
};

export default function PublicAnalysis() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [cpf, setCpf] = useState("");
  const [rent, setRent] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [keySet, setKeySet] = useState(true);
  const [cpfInfo, setCpfInfo] = useState(null);

  const onCpfBlur = async () => {
    const digits = cpf.replace(/\D/g, "");
    if (digits.length !== 11) return;
    try { const { data } = await api.get(`/cpf/lookup?cpf=${digits}`); setCpfInfo(data); } catch { setCpfInfo(null); }
  };

  const loadHistory = () => api.get("/analysis/public").then((r) => setHistory(r.data));
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/settings").then((r) => setKeySet(!!r.data.api_keys?.transparencia_api_key));
    loadHistory();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!clientId && !cpf) { toast.error("Selecione um cliente ou informe o CPF"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/analysis/public", { client_id: clientId || null, cpf: cpf || null, rent_amount: rent ? parseFloat(rent) : null });
      setResult(data);
      toast.success("Análise concluída");
      loadHistory();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro na análise"); }
    finally { setLoading(false); }
  };

  const openHistory = async (id) => {
    const { data } = await api.get(`/analysis/public/${id}`);
    setResult(data);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const setReceita = async (v) => {
    const { data } = await api.patch(`/analysis/public/${result.analysis_id}/receita`, { receita_status: v });
    setResult(data); loadHistory();
    toast.success("Situação na Receita registrada");
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="public-analysis-page">
      <div className="mb-8">
        <div className="eyebrow mb-1">Fontes públicas gratuitas</div>
        <h1 className="font-display text-4xl tracking-tight">Análise por CPF</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Cruza o CPF com bases públicas (validação, Portal da Transparência e Receita Federal) e consolida num semáforo com histórico por cliente.</p>
      </div>

      {!keySet && (
        <div className="mb-6 p-4 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900 flex items-start gap-3" data-testid="pa-key-warning">
          <Warning size={18} className="shrink-0 mt-0.5" />
          <div>
            <strong>Chave do Portal da Transparência não configurada.</strong> A análise rodará só com a validação do CPF.{" "}
            Cadastre-se grátis em <a className="underline" href="https://portaldatransparencia.gov.br/api-de-dados/cadastrar-email" target="_blank" rel="noreferrer">portaldatransparencia.gov.br</a> e cole a chave em <Link to="/settings" className="underline">Configuração do Site</Link>.
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-4">
        <form onSubmit={submit} className="card-flat p-6 lg:col-span-2 self-start" data-testid="pa-form">
          <div className="eyebrow mb-4">Consulta</div>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Cliente cadastrado</Label>
              <Select value={clientId} onValueChange={(v) => { setClientId(v); setCpf(""); }}>
                <SelectTrigger data-testid="pa-client"><SelectValue placeholder="Escolha o cliente" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.name} — {c.cpf}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="text-center text-[11px] text-slate-400">ou</div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">CPF avulso</Label>
              <Input data-testid="pa-cpf" value={cpf} onChange={(e) => { setCpf(e.target.value); setClientId(""); setCpfInfo(null); }} onBlur={onCpfBlur} placeholder="000.000.000-00" className="mono" />
              {cpfInfo && <div className={`text-[11px] mt-1 ${cpfInfo.valid ? "text-emerald-700" : "text-red-600"}`} data-testid="pa-cpf-info">{cpfInfo.valid ? `CPF válido · ${cpfInfo.region}${cpfInfo.name ? ` · ${cpfInfo.name}` : ""}` : `CPF inválido — ${cpfInfo.reason}`}</div>}
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Valor do aluguel (opcional)</Label>
              <Input data-testid="pa-rent" type="number" value={rent} onChange={(e) => setRent(e.target.value)} placeholder="2500" />
            </div>
            <Button data-testid="pa-submit" type="submit" disabled={loading} className="btn-primary w-full h-11">
              <MagnifyingGlass size={16} className="mr-2" /> {loading ? "Consultando bases públicas…" : "Analisar CPF"}
            </Button>
            <div className="text-[11px] text-slate-400 leading-relaxed space-y-1">
              <div className="flex items-center gap-1.5"><IdentificationCard size={12} /> Validação algorítmica do CPF + região de emissão</div>
              <div className="flex items-center gap-1.5"><Bank size={12} /> Portal da Transparência: servidores, benefícios, sanções, PEP, contratos</div>
              <div className="flex items-center gap-1.5"><Scales size={12} /> Receita Federal: verificação manual com registro</div>
            </div>
          </div>
        </form>

        <div className="lg:col-span-3 space-y-4">
          {result && <ResultCard r={result} onReceita={setReceita} />}

          <div className="card-flat p-6" data-testid="pa-history">
            <div className="eyebrow mb-3">Histórico</div>
            {history.length === 0 ? (
              <div className="text-sm text-slate-400 py-4">Sem consultas ainda.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-slate-500 eyebrow">
                  <tr><th className="text-left py-2">Cliente</th><th className="text-left py-2">CPF</th><th className="text-left py-2">Semáforo</th><th className="text-left py-2">Receita</th><th className="text-left py-2">Data</th></tr>
                </thead>
                <tbody>
                  {history.slice(0, 15).map((h) => (
                    <tr key={h.analysis_id} className="border-t border-[var(--line)] hover:bg-slate-50 cursor-pointer" onClick={() => openHistory(h.analysis_id)} data-testid={`pa-history-${h.analysis_id}`}>
                      <td className="py-2 font-medium">{h.client_name || <span className="text-slate-400">CPF avulso</span>}</td>
                      <td className="py-2 mono text-slate-500">{h.cpf}</td>
                      <td className="py-2"><span className={`status-pill ${SEM[h.summary.semaforo].cls}`}>{SEM[h.summary.semaforo].label}</span></td>
                      <td className="py-2 text-slate-600">{RECEITA[h.receita_status]}</td>
                      <td className="py-2 text-slate-500">{fmtDate(h.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ResultCard({ r, onReceita }) {
  const s = SEM[r.summary.semaforo];
  const tr = r.transparencia;
  const f = tr?.flags || {};
  return (
    <div className="card-flat p-6" data-testid="pa-result">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="eyebrow">Resultado</div>
          <div className="font-display text-2xl mt-1">{r.client_name || tr?.nome || "CPF avulso"}</div>
          <div className="text-xs text-slate-500 mono">{r.cpf}{r.cpf_check.region ? ` · emitido em ${r.cpf_check.region}` : ""}</div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{ borderColor: s.color, color: s.color }} data-testid="pa-semaforo">
          <s.Icon size={18} weight="fill" /> <span className="text-sm font-medium">{s.label}</span>
        </div>
      </div>

      <ul className="mt-5 space-y-2" data-testid="pa-points">
        {r.summary.pontos.map((p, i) => {
          const P = SEM[p.level];
          return (
            <li key={i} className="flex items-start gap-2 text-sm">
              <P.Icon size={16} weight="fill" style={{ color: P.color }} className="shrink-0 mt-0.5" />
              <span className="text-slate-700">{p.text}</span>
            </li>
          );
        })}
      </ul>

      {tr?.ok && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-5" data-testid="pa-flags">
          <Flag on={f.servidor} Icon={UserCircleGear} label="Servidor federal" good />
          <Flag on={f.beneficiario_social} Icon={HandCoins} label="Benefício social" warn />
          <Flag on={f.sancionado} Icon={Scales} label="Sanções" bad />
          <Flag on={f.pep} Icon={Warning} label="PEP" warn />
          <Flag on={f.contratado_governo} Icon={Buildings} label="Contratos c/ governo" good />
          <Flag on={f.favorecido_despesas} Icon={Bank} label="Favorecido de despesas" good />
        </div>
      )}

      {tr?.ok && (tr.servidores.length > 0 || tr.remuneracao) && (
        <Section title="Vínculo público e renda">
          {tr.servidores.map((sv, i) => (
            <div key={i} className="text-sm text-slate-700">{sv.cargo || sv.tipo} — {sv.orgao} {sv.uf ? `(${sv.uf})` : ""} · {sv.situacao}{sv.afastado ? " · afastado" : ""}</div>
          ))}
          {tr.remuneracao && <div className="text-sm mt-1">Remuneração líquida <strong>{fmtBRL(tr.remuneracao.liquida)}</strong> (bruta {fmtBRL(tr.remuneracao.bruta)}) · ref. {tr.remuneracao.mes_ano}</div>}
        </Section>
      )}
      {tr?.ok && tr.sancoes.length > 0 && (
        <Section title="Sanções" tone="danger">
          {tr.sancoes.map((x, i) => <div key={i} className="text-sm">{x.fonte} · {x.tipo || "—"} · {x.orgao || ""} · {x.data || ""}</div>)}
        </Section>
      )}
      {tr?.ok && tr.peps.length > 0 && (
        <Section title="Pessoa Exposta Politicamente" tone="warn">
          {tr.peps.map((x, i) => <div key={i} className="text-sm">{x.funcao} — {x.orgao} · {x.inicio}{x.fim ? ` → ${x.fim}` : " (atual)"}</div>)}
        </Section>
      )}
      {tr?.ok && tr.contratos.length > 0 && (
        <Section title="Contratos com o governo">
          {tr.contratos.map((x, i) => <div key={i} className="text-sm">{x.orgao} · {x.objeto} · {x.valor ? fmtBRL(x.valor) : ""}</div>)}
        </Section>
      )}

      <div className="mt-5 p-4 rounded-md border border-[var(--line)] bg-slate-50" data-testid="pa-receita">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="eyebrow">Receita Federal — situação cadastral</div>
            <div className="text-xs text-slate-500 mt-1">A Receita exige captcha; abra a consulta, confira e registre o resultado aqui.</div>
          </div>
          <a href={r.receita_url} target="_blank" rel="noreferrer" data-testid="pa-receita-link">
            <Button size="sm" variant="outline"><ArrowSquareOut size={14} className="mr-1" /> Consultar na Receita</Button>
          </a>
        </div>
        <div className="mt-3 max-w-xs">
          <Select value={r.receita_status} onValueChange={onReceita}>
            <SelectTrigger data-testid="pa-receita-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(RECEITA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="text-[10px] text-slate-400 mt-3">Consulta em {fmtDate(r.created_at)} por {r.created_by}. Fontes: algoritmo CPF{r.sources.portal_transparencia ? ", Portal da Transparência (gov.br)" : ""}, Receita Federal (manual).</div>
    </div>
  );
}

function Flag({ on, Icon, label, good, warn, bad }) {
  const color = !on ? "#94a3b8" : bad ? "#DC2626" : warn ? "#D97706" : good ? "#059669" : "#0F172A";
  return (
    <div className={`flex items-center gap-2 p-2.5 rounded-md border text-xs ${on ? "bg-white border-slate-200" : "bg-slate-50 border-slate-100 text-slate-400"}`}>
      <Icon size={16} weight={on ? "fill" : "regular"} style={{ color }} />
      <span className={on ? "text-slate-800 font-medium" : ""}>{label}</span>
      <span className="ml-auto mono text-[10px]" style={{ color }}>{on ? "SIM" : "não"}</span>
    </div>
  );
}

function Section({ title, tone, children }) {
  const cls = tone === "danger" ? "bg-red-50 border-red-200" : tone === "warn" ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-100";
  return (
    <div className={`mt-4 p-3 rounded-md border ${cls}`}>
      <div className="eyebrow mb-1.5">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
